import React, { useState, useEffect } from 'react';
import './ClassComparisonStandardized.css';
import SupabaseDataService from '../../lib/supabaseDataService.js';
import DataEditor from './DataEditor.js';

const ClassComparisonStandardized = ({ data, className }) => {
  const [selectedClasses, setSelectedClasses] = useState([]);
  const [selectedSubjects, setSelectedSubjects] = useState([]);
  const [selectedTestTypes, setSelectedTestTypes] = useState([]);
  const [selectedAcademicYears, setSelectedAcademicYears] = useState([]);
  const [sortBy, setSortBy] = useState('name'); // 'name', 'median', 'mean', 'iqr'
  const [showOutliers, setShowOutliers] = useState(true);
  const [showMean, setShowMean] = useState(true);
  const [comparisonMode, setComparisonMode] = useState('class'); // 'class', 'subject', 'test', 'year'

  // Color schemes for different dimensions
  const classColors = {
    '1A': '#ef4444', '1B': '#3b82f6', '1C': '#eab308', '1D': '#10b981',
    '2A': '#8b5cf6', '2B': '#f97316', '2C': '#06b6d4', '2D': '#ec4899',
    '3A': '#84cc16', '3B': '#a855f7', '3C': '#f59e0b', '3D': '#14b8a6'
  };

  const subjectColors = {
    '中文': '#ef4444', '英文': '#3b82f6', '數學': '#eab308', '綜合科學': '#10b981',
    '歷史': '#8b5cf6', '地理': '#f97316', '電腦': '#06b6d4', '視覺藝術': '#ec4899',
    '音樂': '#84cc16', '體育': '#a855f7'
  };

  const testTypeColors = {
    'UT1': '#ef4444', 'UT2': '#3b82f6', 'E1': '#eab308', 'E2': '#10b981'
  };

  const academicYearColors = {
    '2020-2021': '#ef4444', 
    '2021-2022': '#3b82f6', 
    '2022-2023': '#eab308', 
    '2023-2024': '#10b981', 
    '2024-2025': '#8b5cf6', 
    '2025-2026': '#f97316'
  };

  // State for available options loaded from database
  const [availableClasses, setAvailableClasses] = useState([]);
  const [availableSubjects, setAvailableSubjects] = useState([]);
  const [availableAcademicYears, setAvailableAcademicYears] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  
  // Static test types (these are consistent)
  const availableTestTypes = ['UT1', 'UT2', 'E1', 'E2'];

  // Load available options from database
  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      try {
        const [classes, subjects, years] = await Promise.all([
          SupabaseDataService.getAvailableClasses(),
          SupabaseDataService.getAvailableSubjects(),
          SupabaseDataService.getAvailableAcademicYears()
        ]);
        
        setAvailableClasses(classes);
        setAvailableSubjects(subjects);
        setAvailableAcademicYears(years);
      } catch (error) {
        console.error('Error loading data:', error);
        // Set fallback data
        setAvailableClasses(['1A', '1B', '1C', '1D', '2A', '2B', '2C', '2D', '3A', '3B', '3C', '3D']);
        setAvailableSubjects(['中文', '英文', '數學', '綜合科學', '歷史', '地理', '電腦', '視覺藝術', '音樂', '體育']);
        setAvailableAcademicYears(['2020-2021', '2021-2022', '2022-2023', '2023-2024', '2024-2025', '2025-2026']);
      }
      setIsLoading(false);
    };
    
    loadData();
  }, []);

  // Initialize selections - start with empty selections for clean view
  useEffect(() => {
    setSelectedClasses([]);
    setSelectedSubjects([]);
    setSelectedTestTypes([]);
    setSelectedAcademicYears([]);
  }, []);

  // State for comparison data
  const [comparisonData, setComparisonData] = useState([]);
  const [dataLoading, setDataLoading] = useState(false);
  
  // State for data editing
  const [showDataEditor, setShowDataEditor] = useState(false);
  const [editableData, setEditableData] = useState([]);
  const [dataSource, setDataSource] = useState('database'); // 'database' or 'imported'

  // Generate comparison data based on selections
  const generateComparisonData = async () => {
    if (!selectedClasses.length && !selectedSubjects.length && !selectedTestTypes.length && !selectedAcademicYears.length) {
      setComparisonData([]);
      return;
    }

    setDataLoading(true);
    try {
      const filters = {
        classes: selectedClasses,
        subjects: selectedSubjects,
        testTypes: selectedTestTypes,
        academicYears: selectedAcademicYears
      };
      
      const data = await SupabaseDataService.generateComparisonData(filters, comparisonMode);
      
      // If no real data is available, fall back to demo data
      if (!data || data.length === 0) {
        console.log('No real data available, using demo data');
        const demoData = generateDemoComparisonData();
        setComparisonData(demoData);
      } else {
        console.log('Using real data from Supabase:', data.length, 'items');
        setComparisonData(data);
      }
    } catch (error) {
      console.error('Error generating comparison data:', error);
      console.log('Falling back to demo data due to error');
      const demoData = generateDemoComparisonData();
      setComparisonData(demoData);
    }
    setDataLoading(false);
  };

  // Update comparison data when selections change
  useEffect(() => {
    if (!isLoading) {
      generateComparisonData();
    }
  }, [selectedClasses, selectedSubjects, selectedTestTypes, selectedAcademicYears, comparisonMode, isLoading]);

  // Legacy function for demo purposes (keeping for backward compatibility)
  const generateDemoComparisonData = () => {
    const comparisonData = [];
    
    // Generate sample data for demonstration
    // In real implementation, this would come from your database
    selectedClasses.forEach(className => {
      selectedSubjects.forEach(subject => {
        selectedTestTypes.forEach(testType => {
          selectedAcademicYears.forEach(academicYear => {
            // Generate realistic sample data
            const baseScore = 70 + Math.random() * 20; // Base score between 70-90
            const variation = 10 + Math.random() * 10; // Variation between 10-20
            
            const scores = Array.from({ length: 25 + Math.floor(Math.random() * 15) }, () => {
              return Math.max(0, Math.min(100, baseScore + (Math.random() - 0.5) * variation));
            });

            const stats = calculateBoxPlotStats(scores);
            
            comparisonData.push({
              id: `${className}-${subject}-${testType}-${academicYear}`,
              name: `${className} - ${subject} - ${testType}`,
              className,
              subject,
              testType,
              academicYear,
              data: scores,
              // Spread stats directly onto the item for compatibility
              ...stats,
              color: getColorForComparison(className, subject, testType, academicYear)
            });
          });
        });
      });
    });

    return comparisonData;
  };

  const getColorForComparison = (className, subject, testType, academicYear) => {
    switch (comparisonMode) {
      case 'class':
        return classColors[className] || '#6b7280';
      case 'subject':
        return subjectColors[subject] || '#6b7280';
      case 'test':
        return testTypeColors[testType] || '#6b7280';
      case 'year':
        return academicYearColors[academicYear] || '#6b7280';
      default:
        return '#6b7280';
    }
  };

  const calculateBoxPlotStats = (data) => {
    if (!data || data.length === 0) return null;

    const sorted = [...data].sort((a, b) => a - b);
    const n = sorted.length;
    
    const q1 = sorted[Math.floor(n * 0.25)];
    const median = sorted[Math.floor(n * 0.5)];
    const q3 = sorted[Math.floor(n * 0.75)];
    const min = sorted[0];
    const max = sorted[n - 1];
    const mean = data.reduce((sum, val) => sum + val, 0) / n;
    
    const iqr = q3 - q1;
    const lowerBound = q1 - 1.5 * iqr;
    const upperBound = q3 + 1.5 * iqr;
    
    const outliers = data.filter(val => val < lowerBound || val > upperBound);
    
    return { min, q1, median, q3, max, mean, outliers, iqr };
  };

  const renderUnifiedChart = (comparisonData) => {
    if (!comparisonData || comparisonData.length === 0) {
      return (
        <div className="no-data">
          <div className="no-data-icon">📊</div>
          <h4>請選擇比較項目 Please Select Items to Compare</h4>
          <p>使用上方的選擇器來選擇要比較的班級、科目、考試類型和學年</p>
          <p>Use the selectors above to choose classes, subjects, test types, and academic years to compare</p>
        </div>
      );
    }

    // Filter valid data and calculate global scale
    const validData = comparisonData.filter(item => item && item.data && Array.isArray(item.data) && item.data.length > 0);
    
    if (validData.length === 0) {
      return (
        <div className="no-data">
          <div className="no-data-icon">📊</div>
          <h4>無有效數據 No Valid Data</h4>
          <p>請確保選擇的項目包含有效的數據點</p>
          <p>Please ensure selected items contain valid data points</p>
        </div>
      );
    }
    
    const allScores = validData.flatMap(item => item.data.filter(score => typeof score === 'number' && !isNaN(score)));
    
    if (allScores.length === 0) {
      return (
        <div className="no-data">
          <div className="no-data-icon">📊</div>
          <h4>無數值數據 No Numeric Data</h4>
          <p>選擇的數據中沒有有效的數值</p>
          <p>No valid numeric values found in selected data</p>
        </div>
      );
    }
    
    const globalMin = Math.min(...allScores);
    const globalMax = Math.max(...allScores);
    const globalRange = globalMax - globalMin;

    // Chart dimensions
    const chartWidth = 800;
    const chartHeight = 400;
    const margin = { top: 40, right: 60, bottom: 60, left: 80 };
    const plotWidth = chartWidth - margin.left - margin.right;
    const plotHeight = chartHeight - margin.top - margin.bottom;

    // Scale functions
    const xScale = (value) => margin.left + ((value - globalMin) / globalRange) * plotWidth;
    const yScale = (index) => margin.top + (index * plotHeight / validData.length);

    return (
      <div className="unified-chart-wrapper">
        <h4>標準化比較圖表 Standardized Comparison Chart</h4>
        <div className="unified-chart-container">
          <svg width={chartWidth} height={chartHeight} className="unified-chart-svg">
            {/* Chart title */}
            <text x={chartWidth / 2} y={20} className="chart-title" textAnchor="middle">
              {getComparisonTitle()}
            </text>

            {/* X-axis */}
            <line x1={margin.left} y1={chartHeight - margin.bottom} x2={chartWidth - margin.right} y2={chartHeight - margin.bottom} stroke="#e5e7eb" strokeWidth="2" />
            
            {/* X-axis labels */}
            {[globalMin, globalMin + globalRange * 0.25, globalMin + globalRange * 0.5, globalMin + globalRange * 0.75, globalMax].map((tick, i) => (
              <g key={i}>
                <line x1={xScale(tick)} y1={chartHeight - margin.bottom} x2={xScale(tick)} y2={chartHeight - margin.bottom + 5} stroke="#e5e7eb" strokeWidth="1" />
                <text x={xScale(tick)} y={chartHeight - margin.bottom + 20} className="axis-label" textAnchor="middle">
                  {tick.toFixed(0)}
                </text>
              </g>
            ))}

            {/* X-axis title */}
            <text x={chartWidth / 2} y={chartHeight - 10} className="axis-label" textAnchor="middle">
              成績分數 Score
            </text>

            {/* Box plots */}
            {validData.map((item, index) => {
              const y = yScale(index) + plotHeight / validData.length / 2;
              const boxHeight = Math.min(30, plotHeight / validData.length - 10);
              
              // Handle both nested stats and direct properties
              const stats = item.stats || item;
              
              // Validate required statistics
              if (!stats || typeof stats.min === 'undefined' || typeof stats.max === 'undefined' || 
                  typeof stats.q1 === 'undefined' || typeof stats.q3 === 'undefined' || 
                  typeof stats.median === 'undefined') {
                return null;
              }

              const x1 = xScale(stats.q1);
              const x2 = xScale(stats.q3);
              const xMedian = xScale(stats.median);
              const xMean = xScale(stats.mean);
              const xMin = xScale(stats.min);
              const xMax = xScale(stats.max);

              return (
                <g key={item.id}>
                  {/* Whiskers */}
                  <line x1={xMin} y1={y} x2={xMax} y2={y} stroke="#64748b" strokeWidth="2" />
                  
                  {/* Box */}
                  <rect x={x1} y={y - boxHeight/2} width={x2 - x1} height={boxHeight} 
                        fill={item.color} stroke="#374151" strokeWidth="1" />
                  
                  {/* Median line */}
                  <line x1={xMedian} y1={y - boxHeight/2} x2={xMedian} y2={y + boxHeight/2} 
                        stroke="#ffffff" strokeWidth="2" />
                  
                  {/* Mean marker */}
                  {showMean && (
                    <circle cx={xMean} cy={y} r="3" fill="#374151" stroke="#ffffff" strokeWidth="1" />
                  )}
                  
                  {/* Outliers */}
                  {showOutliers && stats.outliers.map((outlier, outlierIndex) => (
                    <circle key={outlierIndex} cx={xScale(outlier)} cy={y} r="2" 
                            fill="#f59e0b" stroke="#ffffff" strokeWidth="1" />
                  ))}
                  
                  {/* Label */}
                  <text x={margin.left - 10} y={y + 4} className="subject-label" textAnchor="end">
                    {getComparisonLabel(item)}
                  </text>
                </g>
              );
            })}
          </svg>
        </div>
      </div>
    );
  };

  const getComparisonTitle = () => {
    const modeNames = {
      'class': '班級比較 Class Comparison',
      'subject': '科目比較 Subject Comparison', 
      'test': '考試類型比較 Test Type Comparison',
      'year': '學年比較 Academic Year Comparison'
    };
    return modeNames[comparisonMode] || '多維度比較 Multi-dimensional Comparison';
  };

  const getComparisonLabel = (item) => {
    switch (comparisonMode) {
      case 'class':
        return `${item.className} (${item.subject})`;
      case 'subject':
        return `${item.subject} (${item.className})`;
      case 'test':
        return `${item.testType} (${item.className}-${item.subject})`;
      case 'year':
        return `${item.academicYear} (${item.className}-${item.subject})`;
      default:
        return `${item.className}-${item.subject}-${item.testType}`;
    }
  };

  const renderStatisticsTable = (comparisonData) => {
    if (!comparisonData || comparisonData.length === 0) {
      return (
        <div className="statistics-table">
          <h4>統計數據表 Statistical Data Table</h4>
          <div className="no-data">
            <p>請先選擇比較項目以查看統計數據</p>
            <p>Please select comparison items to view statistical data</p>
          </div>
        </div>
      );
    }

    return (
      <div className="statistics-table">
        <h4>統計數據表 Statistical Data Table</h4>
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>項目 Item</th>
                <th>樣本數 Count</th>
                <th>最小值 Min</th>
                <th>Q1</th>
                <th>中位數 Median</th>
                <th>Q3</th>
                <th>最大值 Max</th>
                <th>平均數 Mean</th>
                <th>IQR</th>
                <th>異常值 Outliers</th>
              </tr>
            </thead>
            <tbody>
              {comparisonData.filter(item => item && typeof item === 'object').map((item, index) => {
                // Handle both nested stats and direct properties
                const stats = item.stats || item;
                const dataLength = item.data ? item.data.length : 0;
                const outlierCount = stats.outliers ? stats.outliers.length : 0;
                
                return (
                  <tr key={item.id || `item-${index}`}>
                    <td>
                      <span className="subject-color-dot" style={{ backgroundColor: item.color || '#3b82f6' }}></span>
                      {getComparisonLabel(item)}
                    </td>
                    <td>{dataLength}</td>
                    <td>{stats.min ? stats.min.toFixed(1) : 'N/A'}</td>
                    <td>{stats.q1 ? stats.q1.toFixed(1) : 'N/A'}</td>
                    <td>{stats.median ? stats.median.toFixed(1) : 'N/A'}</td>
                    <td>{stats.q3 ? stats.q3.toFixed(1) : 'N/A'}</td>
                    <td>{stats.max ? stats.max.toFixed(1) : 'N/A'}</td>
                    <td>{stats.mean ? stats.mean.toFixed(1) : 'N/A'}</td>
                    <td>{stats.iqr ? stats.iqr.toFixed(1) : 'N/A'}</td>
                    <td>{outlierCount}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // comparisonData is now managed in state and updated via useEffect

  // Reset function to clear all selections
  const handleReset = () => {
    setSelectedClasses([]);
    setSelectedSubjects([]);
    setSelectedTestTypes([]);
    setSelectedAcademicYears([]);
    setComparisonMode('class');
    setSortBy('name');
    setShowOutliers(true);
    setShowMean(true);
  };

  // Quick select functions
  const handleSelectAllClasses = () => {
    setSelectedClasses(availableClasses);
  };

  const handleSelectAllSubjects = () => {
    setSelectedSubjects(availableSubjects);
  };

  const handleSelectAllTestTypes = () => {
    setSelectedTestTypes(availableTestTypes);
  };

  const handleSelectCurrentYear = () => {
    setSelectedAcademicYears(['2024-2025']);
  };

  // Quick demo data loader
  const handleLoadDemoData = () => {
    setSelectedClasses(['1A', '1B', '1C']);
    setSelectedSubjects(['中文', '英文', '數學']);
    setSelectedTestTypes(['UT1', 'UT2']);
    setSelectedAcademicYears(['2024-2025']);
    setComparisonMode('class');
  };

  // JSON Export functionality
  const generateFileName = () => {
    const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
    const mode = comparisonMode;
    const selections = {
      classes: selectedClasses.length,
      subjects: selectedSubjects.length,
      tests: selectedTestTypes.length,
      years: selectedAcademicYears.length
    };
    
    const selectionSummary = Object.entries(selections)
      .filter(([_, count]) => count > 0)
      .map(([key, count]) => `${count}${key.charAt(0)}`)
      .join('-');
    
    return `BoxPlot_${mode}Comparison_${selectionSummary}_${timestamp}.json`;
  };

  const handleExportJSON = () => {
    const exportData = {
      metadata: {
        exportDate: new Date().toISOString(),
        comparisonMode: comparisonMode,
        dataSource: dataSource,
        description: `Box plot comparison data exported from MyClass system`,
        selections: {
          classes: selectedClasses,
          subjects: selectedSubjects,
          testTypes: selectedTestTypes,
          academicYears: selectedAcademicYears
        }
      },
      rawData: editableData.length > 0 ? editableData : [], // Use imported data if available
      comparisonData: comparisonData,
      statistics: comparisonData.filter(item => item).map(item => {
        const stats = item.stats || item;
        return {
          name: item.name || 'Unknown',
          count: item.data ? item.data.length : 0,
          min: stats.min || 0,
          q1: stats.q1 || 0,
          median: stats.median || 0,
          q3: stats.q3 || 0,
          max: stats.max || 0,
          mean: stats.mean || 0,
          iqr: stats.iqr || 0,
          outliers: stats.outliers || []
        };
      })
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = generateFileName();
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // JSON Import functionality
  const handleImportJSON = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const importedData = JSON.parse(e.target.result);
        
        // Validate imported data structure
        if (importedData.comparisonData || importedData.rawData) {
          setEditableData(importedData.rawData || []);
          setDataSource('imported');
          
          // If comparison data exists, use it directly
          if (importedData.comparisonData) {
            setComparisonData(importedData.comparisonData);
          }
          
          // Restore selections if available
          if (importedData.metadata && importedData.metadata.selections) {
            const { selections } = importedData.metadata;
            setSelectedClasses(selections.classes || []);
            setSelectedSubjects(selections.subjects || []);
            setSelectedTestTypes(selections.testTypes || []);
            setSelectedAcademicYears(selections.academicYears || []);
            setComparisonMode(importedData.metadata.comparisonMode || 'class');
          }
          
          alert(`✅ 數據導入成功！\nData imported successfully!\n\n文件: ${file.name}\n數據點: ${importedData.rawData?.length || importedData.comparisonData?.length || 0}`);
          setShowDataEditor(true); // Show editor after import
        } else {
          alert('❌ 無效的JSON格式\nInvalid JSON format. Please ensure the file contains comparison data.');
        }
      } catch (error) {
        alert(`❌ 文件解析錯誤\nFile parsing error: ${error.message}`);
      }
    };
    reader.readAsText(file);
    
    // Reset file input
    event.target.value = '';
  };

  // Handle data changes from editor
  const handleDataEditorChange = (newData) => {
    setEditableData(newData);
    setComparisonData(newData);
  };

  if (isLoading) {
    return (
      <div className={`class-comparison-standardized ${className || ''}`}>
        <div className="comparison-header">
          <h2>班級標準化比較 Class Standardized Comparison</h2>
          <p className="class-info">多維度成績分析 Multi-dimensional Performance Analysis</p>
        </div>
        <div className="loading-state">
          <div className="loading-icon">⏳</div>
          <h3>載入中 Loading...</h3>
          <p>正在從資料庫載入真實學生成績數據</p>
          <p>Loading real student marks data from database</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`class-comparison-standardized ${className || ''}`}>
      <div className="comparison-header">
        <h2>班級標準化比較 Class Standardized Comparison</h2>
        <p className="class-info">多維度成績分析 Multi-dimensional Performance Analysis</p>
        <p className="description">
          通過選擇不同的維度（班級、科目、考試類型、學年）來進行標準化的盒鬚圖比較分析
          <br />
          <small style={{color: '#10b981', fontWeight: 'bold'}}>✓ 使用真實資料庫數據 Using Real Database Data</small>
        </p>
      </div>

      {/* Comparison Mode Selector */}
      <div className="comparison-mode-selector">
        <h4>比較模式 Comparison Mode</h4>
        <div className="mode-buttons">
          {[
            { id: 'class', name: '班級比較 Class', description: '按班級分組比較' },
            { id: 'subject', name: '科目比較 Subject', description: '按科目分組比較' },
            { id: 'test', name: '考試比較 Test', description: '按考試類型分組比較' },
            { id: 'year', name: '學年比較 Year', description: '按學年分組比較' }
          ].map(mode => (
            <button
              key={mode.id}
              className={`mode-button ${comparisonMode === mode.id ? 'active' : ''}`}
              onClick={() => setComparisonMode(mode.id)}
            >
              <div className="mode-name">{mode.name}</div>
              <div className="mode-description">{mode.description}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Multi-dimensional Controls */}
      <div className="comparison-controls">
        {/* Control Header with Reset Button */}
        <div className="controls-header">
          <h3>選擇比較項目 Select Comparison Items</h3>
          <div className="control-buttons">
            <button className="reset-button" onClick={handleReset}>
              🔄 重置 Reset
            </button>
            <button className="quick-select-button" onClick={() => {
              handleSelectAllClasses();
              handleSelectAllSubjects();
              handleSelectAllTestTypes();
              handleSelectCurrentYear();
            }}>
              ⚡ 快速選擇 Quick Select
            </button>
          </div>
        </div>

        {/* Data Import/Export Section */}
        <div className="data-management-section">
          <h4>📁 數據管理 Data Management</h4>
          <div className="data-buttons">
            <button 
              className="export-button" 
              onClick={handleExportJSON}
              disabled={comparisonData.length === 0}
              title="Export current comparison data as JSON file"
            >
              📤 匯出 JSON Export
            </button>
            
            <label className="import-button">
              📥 匯入 JSON Import
              <input
                type="file"
                accept=".json"
                onChange={handleImportJSON}
                style={{ display: 'none' }}
              />
            </label>
            
            <button 
              className="editor-button" 
              onClick={() => setShowDataEditor(!showDataEditor)}
              disabled={comparisonData.length === 0 && editableData.length === 0}
              title="Open data editor to view and edit records"
            >
              ✏️ {showDataEditor ? '關閉編輯器 Close Editor' : '數據編輯器 Data Editor'}
            </button>
            
            <button 
              className="demo-button" 
              onClick={handleLoadDemoData}
              title="Load sample data to see box plot visualization"
            >
              🎯 載入示範數據 Load Demo Data
            </button>
          </div>
          
          {dataSource === 'imported' && (
            <div className="data-source-indicator">
              <span className="imported-indicator">
                📂 使用匯入數據 Using Imported Data
              </span>
            </div>
          )}
        </div>

        {/* Class Selection */}
        <div className="control-group">
          <div className="control-group-header">
            <h4>班級選擇 Class Selection</h4>
            <button 
              className="select-all-button"
              onClick={handleSelectAllClasses}
            >
              {selectedClasses.length === availableClasses.length ? '取消全選' : '全選'}
            </button>
          </div>
          <div className="selection-grid">
            {availableClasses.map(className => (
              <label key={className} className="selection-item">
                <input
                  type="checkbox"
                  checked={selectedClasses.includes(className)}
                  onChange={() => setSelectedClasses(prev => 
                    prev.includes(className) 
                      ? prev.filter(c => c !== className)
                      : [...prev, className]
                  )}
                />
                <span className="color-indicator" style={{ backgroundColor: classColors[className] }}></span>
                <span>{className}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Subject Selection */}
        <div className="control-group">
          <div className="control-group-header">
            <h4>科目選擇 Subject Selection</h4>
            <button 
              className="select-all-button"
              onClick={handleSelectAllSubjects}
            >
              {selectedSubjects.length === availableSubjects.length ? '取消全選' : '全選'}
            </button>
          </div>
          <div className="selection-grid">
            {availableSubjects.map(subject => (
              <label key={subject} className="selection-item">
                <input
                  type="checkbox"
                  checked={selectedSubjects.includes(subject)}
                  onChange={() => setSelectedSubjects(prev => 
                    prev.includes(subject) 
                      ? prev.filter(s => s !== subject)
                      : [...prev, subject]
                  )}
                />
                <span className="color-indicator" style={{ backgroundColor: subjectColors[subject] }}></span>
                <span>{subject}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Test Type Selection */}
        <div className="control-group">
          <div className="control-group-header">
            <h4>考試類型 Test Type</h4>
            <button 
              className="select-all-button"
              onClick={handleSelectAllTestTypes}
            >
              {selectedTestTypes.length === availableTestTypes.length ? '取消全選' : '全選'}
            </button>
          </div>
          <div className="selection-grid">
            {availableTestTypes.map(testType => (
              <label key={testType} className="selection-item">
                <input
                  type="checkbox"
                  checked={selectedTestTypes.includes(testType)}
                  onChange={() => setSelectedTestTypes(prev => 
                    prev.includes(testType) 
                      ? prev.filter(t => t !== testType)
                      : [...prev, testType]
                  )}
                />
                <span className="color-indicator" style={{ backgroundColor: testTypeColors[testType] }}></span>
                <span>{testType}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Academic Year Selection */}
        <div className="control-group">
          <div className="control-group-header">
            <h4>學年 Academic Year</h4>
            <button 
              className="select-all-button"
              onClick={handleSelectCurrentYear}
            >
              {selectedAcademicYears.includes('2024-2025') ? '取消選擇' : '選擇當前學年'}
            </button>
          </div>
          <div className="selection-grid">
            {availableAcademicYears.map(year => (
              <label key={year} className="selection-item">
                <input
                  type="checkbox"
                  checked={selectedAcademicYears.includes(year)}
                  onChange={() => setSelectedAcademicYears(prev => 
                    prev.includes(year) 
                      ? prev.filter(y => y !== year)
                      : [...prev, year]
                  )}
                />
                <span className="color-indicator" style={{ backgroundColor: academicYearColors[year] }}></span>
                <span>{year}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Display Options */}
        <div className="control-group">
          <h4>顯示選項 Display Options</h4>
          <div className="display-options">
            <label className="option-checkbox">
              <input
                type="checkbox"
                checked={showMean}
                onChange={(e) => setShowMean(e.target.checked)}
              />
              <span>顯示平均數 Show Mean</span>
            </label>
            <label className="option-checkbox">
              <input
                type="checkbox"
                checked={showOutliers}
                onChange={(e) => setShowOutliers(e.target.checked)}
              />
              <span>顯示異常值 Show Outliers</span>
            </label>
          </div>
        </div>

        {/* Sort Options */}
        <div className="control-group">
          <h4>排序方式 Sort By</h4>
          <select 
            className="sort-select"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
          >
            <option value="name">按名稱 By Name</option>
            <option value="median">按中位數 By Median</option>
            <option value="mean">按平均數 By Mean</option>
            <option value="iqr">按四分位距 By IQR</option>
          </select>
        </div>
      </div>

      {/* Chart Display */}
      <div className="comparison-chart">
        {dataLoading ? (
          <div className="chart-loading">
            <div className="loading-icon">📊</div>
            <h4>正在載入比較數據 Loading Comparison Data...</h4>
            <p>正在從 Supabase 資料庫獲取學生成績數據</p>
          </div>
        ) : (
          renderUnifiedChart(comparisonData)
        )}
      </div>

      {/* Statistics Table */}
      {dataLoading ? (
        <div className="statistics-table">
          <h4>統計數據表 Statistical Data Table</h4>
          <div className="chart-loading">
            <p>正在載入統計數據 Loading statistical data...</p>
          </div>
        </div>
      ) : (
        renderStatisticsTable(comparisonData)
      )}

      {/* Legend */}
      <div className="comparison-legend">
        <h4>圖例說明 Legend</h4>
        <div className="legend-items">
          <div className="legend-item">
            <div className="legend-color box-color"></div>
            <span>盒鬚圖主體 Box Plot Body</span>
          </div>
          <div className="legend-item">
            <div className="legend-color median-color"></div>
            <span>中位數線 Median Line</span>
          </div>
          <div className="legend-item">
            <div className="legend-color mean-color"></div>
            <span>平均數標記 Mean Marker</span>
          </div>
          <div className="legend-item">
            <div className="legend-color outlier-color"></div>
            <span>異常值 Outliers</span>
          </div>
          <div className="legend-item">
            <div className="legend-color whisker-color"></div>
            <span>鬚線 Whiskers</span>
          </div>
        </div>
      </div>

      {/* Data Editor Modal */}
      {showDataEditor && (
        <DataEditor
          data={dataSource === 'imported' ? editableData : comparisonData}
          onDataChange={handleDataEditorChange}
          onClose={() => setShowDataEditor(false)}
          comparisonMode={comparisonMode}
          dataSource={dataSource}
        />
      )}
    </div>
  );
};

export default ClassComparisonStandardized;
